<template>
  <div class="departures">
    <h2 :class="titleClass">{{ title }}</h2>
    <div :class="subTitleClass"v-html="subTitle"></div>

    <template v-if="ready">
      <div
        v-if="departures.length === 0"
        class="departures__no-results error-message"
      >
        <div class="error-message__icon"></div>
        <div class="error-message__text">
          <slot name="noResultsHtml"></slot>
        </div>
      </div>

      <template v-else>
        <div v-if="!hideTravelOptions">
          <div class="departure__types-wrapper">
            <ul class="departures__types">
              <li v-for="type in types" :key="type.id" class="departures__type">
                <button
                  class="departures__type-button btn btn--outline"
                  :class="{ active: activeType.name === type.name }"
                  :disabled="!type.enabled"
                  @click="changeType(type)">
                  {{ type.name }}
                </button>
              </li>
            </ul>
          </div>

          <div
            v-if="activeType.name === firstType.name && locations.length > 1"
            class="departures__location-wrapper">
            <label class="visually-hidden" for="departure-location">Departing from</label>
            <select
              id="departure-location"
              class="departures__location"
              v-if="locations.length"
              v-model="location">
              <option value>Departing from</option>
              <option
                v-for="location in locations"
                :key="location.value"
                :value="location.value">
                {{ location.label }}
              </option>
            </select>
          </div>
        </div>
        <table class="departures__table">
          <thead>
            <tr>
              <th>
                <div class="d-flex" :class="sortOrderClass('date')">
                  Date
                  <div @click="sortBy('date')" class="sort-icon">
                    <div class="tooltip" data-tooltip="Sort by: date"></div>
                  </div>
                </div>
              </th>
              <th>
                <div class="d-flex" :class="sortOrderClass('secondaryInformation')">
                  {{ hideTravelOptions ? 'Airport' : 'Duration' }}
                  <div @click="sortBy('secondaryInformation')" class="sort-icon">
                    <div class="tooltip" :data-tooltip="secondaryInformationTooltipText"></div>
                  </div>
                </div>
              </th>
              <th>
                <div class="d-flex" :class="sortOrderClass('price')">
                  Price
                  <div @click="sortBy('price')" class="sort-icon">
                    <div class="tooltip" data-tooltip="Sort by: price"></div>
                  </div>
                </div>
              </th>
              <th>
                <div class="form-switch">
                  <span>Availability</span>
                  <input class="form-switch-input" type="checkbox" role="switch" id="flexSwitchCheckDefault" v-model="showOnlyAvailable">
                  <label class="form-switch-label tooltip" for="flexSwitchCheckDefault" data-tooltip="View only available tours"></label>
                </div>
              </th>
              <th></th>
            </tr>
          </thead>
          <tbody
            v-for="departure in filteredDepartures"
            :key="departure.moreDetailsUrl"
            class="departures__tbody">
            <tr class="departures__row"
              :class="departure.open ? 'departures__row--open' : ''"
              @click="toggleDetails(departure)">
              <td :class="cellClass">
                  {{ departure.date }}
              </td>
              <td :class="cellClass">
                {{ departure.secondaryInformation }}
              </td>
              <td :class="cellClass">
                <span :class="wasPriceClass"
                  v-if="departure.tourSummary.hasWasFromPrice"
                  >{{ departure.tourSummary.wasPriceFormatted }}</span>
                {{ departure.tourSummary.fromPriceFormatted }}
              </td>
              <td :class="cellClass">
                {{ departure.status }}
              </td>
              <td :class="cellClass">
                <span :class="arrowClass"></span>
              </td>
            </tr>
            <tr v-if="departure.details && departure.open">
              <td colspan="6">
                <div class="d-flex flex-column">
                  <div v-if="departure.available > 0">
                    <a class="departures__book-now btn"
                      :href="departure.details.bookNowLink.url"
                      @click="handleBookNowClick($event, departure)">{{ departure.details.bookNowLink.name }}</a>
                  </div>
                  <YourHoliday :departureDetails="departure.details" :brand="brand"/>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </template>
    </template>

    <template v-if="apiError && !ready">
      <div class="departures__no-results error-message">
        <div class="error-message__icon"></div>
        <div class="error-message__text">
          <slot name="apiUnavailableHtml"></slot>
        </div>
      </div>
    </template>

    <template v-if="types && !firstType">
      <div class="departures__no-results error-message">
        <div class="error-message__icon"></div>
        <div class="error-message__text">
          <slot name="noResultsHtml"></slot>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Vue from "vue";
import YourHoliday from "./YourHoliday.vue";
import axios from "axios";
import {Brand} from './../../../brand.js';

export default {
  components: {
    YourHoliday,
  },

  props: {
    travelOptionsUrl: String,
    hideTravelOptions: Boolean,
    title: String,
    subTitle: String,
    currency: String,
    tourCode: String,
    tourName: String,
    brand: String,
    centeredTitleAndSubtitle: Boolean
  },

  data() {
    return {
      Brand,
      departures: [],
      locations: [],
      location: "",
      locationType: "",
      types: [],
      activeType: {},
      firstType: {},
      ready: false,
      apiError: false,
      sortKey: '',
      sortOrder: 1,
      showOnlyAvailable: false
    };
  },

  watch: {
    activeType: "fetchDepartures",
    location: "fetchDepartures",
  },

  created() {
    this.loadTransportOptions();
  },

  computed: {
    secondaryInformationTooltipText() {
      return this.hideTravelOptions ? 'Sort by: airport' : 'Sort by: duration';
    },
    titleClass() {
      return {
        'departures__title': true,
        'departures__titleTs': this.brand === Brand.Travelsphere && !this.centeredTitleAndSubtitle
      };
    },
    subTitleClass() {
      return {
        'departures__subTitle block-title': true,
        'departures__subTitleTs': this.brand === Brand.Travelsphere && !this.centeredTitleAndSubtitle,
        'departures__centeredSubTitleTs': this.brand === Brand.Travelsphere && this.centeredTitleAndSubtitle
      };
    },
    cellClass() {
      return {
        'departures__cell': true,
        'departures__cellTs': this.brand === Brand.Travelsphere
      };
    },
    wasPriceClass() {
      return {
        'departures__was-price': true,
        'departures__was-priceTs': this.brand === Brand.Travelsphere
      };
    },
    arrowClass() {
      return {
        'departures__arrow': true,
        'departures__arrowTs': this.brand === Brand.Travelsphere
      };
    },
    sortedDepartures() {
      return this.departures.slice().sort((a, b) => {
        let valueA, valueB;

        if (this.sortKey === 'date') {
          valueA = new Date(a.date);
          valueB = new Date(b.date);
        } else if (this.sortKey === 'price') {
          valueA = parseFloat(a.tourSummary.fromPriceFormatted.replace(/[^0-9.-]+/g, ""));
          valueB = parseFloat(b.tourSummary.fromPriceFormatted.replace(/[^0-9.-]+/g, ""));
        } else if (this.sortKey === 'secondaryInformation') {
          valueA = a.secondaryInformation.toLowerCase();
          valueB = b.secondaryInformation.toLowerCase();
        } else {
          return 0;
        }

        if (valueA < valueB) return -1 * this.sortOrder;
        if (valueA > valueB) return 1 * this.sortOrder;
        return 0;
      });
    },
    filteredDepartures() {
      if (this.showOnlyAvailable) {
        return this.sortedDepartures.filter(departure => departure.status !== 'Sold out');
      }
      return this.sortedDepartures;
    }
  },

  methods: {
    getUtmParams() {
      const urlParams = new URLSearchParams(window.location.search);
      const utmParams = {
        utmSource: urlParams.get('utm_source') || null,
        utmMedium: urlParams.get('utm_medium') || null,
      };
      return utmParams;
    },

    async handleBookNowClick(e, departure) {
      e.preventDefault();
      try {
        const dataLayerItem = {
          brand: this.brand,
          departure: departure,
          currency: this.currency,
          tourCode: this.tourCode,
          utmParams: this.getUtmParams()
        };
        GTM.addToCart(dataLayerItem);
      } catch (e) {
        console.error(e);
      }

      window.location.href = departure.details.bookNowLink.url;
    },

    changeType(type) {
      this.activeType = type;
    },

    async fetchDepartures() {
      try {
        const response = await axios.get(this.activeType.value, {
          params: {
            [this.locationType]: this.location,
          },
        });
        this.departures = response.data.departures;

        if (response.data.departurePoints) {
          this.locations = response.data.departurePoints.children;
          this.locationType = response.data.departurePoints.type;
        }

        this.ready = true;
      } catch {
        this.apiError = true;
      }
    },

    toggleDetails(departure) {
      if (departure.open) {
        departure.open = false;
      } else if (departure.details) {
        departure.open = true;
      } else {
        this.loadDetails(departure);
      }
    },

    async loadDetails(departure) {
      try {
        const response = await axios.get(departure.moreDetailsUrl);
        Vue.set(departure, "details", response.data);
        Vue.set(departure, "open", true);
      } catch (e) {
        console.error(e);
      }
    },

    async loadTransportOptions() {
      try {
        const response = await axios.get(this.travelOptionsUrl);
        this.types = response.data;

        const firstActiveType = response.data.find((type) => type.enabled);
        this.firstType = firstActiveType;
        this.activeType = firstActiveType;
      } catch {
        this.apiError = true;
      }
    },

    sortBy(key) {
      if (this.sortKey === key) {
        this.sortOrder = -this.sortOrder;
      } else {
        this.sortKey = key;
        if (key === 'date') {
          this.sortOrder = -1;
        } else {
          this.sortOrder = 1;
        }
      }
    },

    sortOrderClass(key) {
      if (this.sortKey === key) {
        return this.sortOrder === 1 ? "sort-asc" : "sort-desc";
      }
      return "";
    },
  },
};
</script>
